import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Disc3, Globe2, Library as LibraryIcon } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";

const navLinkBase =
  "font-mono text-[10px] uppercase tracking-[0.25em] px-3 py-2 rounded-sm transition-colors inline-flex items-center gap-1.5";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain relative">
      <header
        className="sticky top-0 z-40 bg-[#0A0A0A]/70 backdrop-blur-2xl border-b border-white/10"
        data-testid="app-header"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
          <Link to={user ? "/" : "/feed"} className="flex items-center gap-3 group shrink-0" data-testid="logo-link">
            <span className="relative inline-flex h-9 w-9 items-center justify-center bg-[#FF3B30] rounded-sm">
              <Disc3 className="h-5 w-5 text-white" strokeWidth={2.2} />
            </span>
            <div className="leading-none">
              <div className="font-display font-black text-xl tracking-tighter">LYRIQ</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-1">
                upload · play · sing
              </div>
            </div>
          </Link>

          <nav className="hidden sm:flex items-center gap-1 ml-4 flex-1">
            <NavLink
              to="/feed"
              className={({ isActive }) =>
                `${navLinkBase} ${isActive ? "text-white bg-white/5" : "text-white/50 hover:text-white"}`
              }
              data-testid="nav-feed-link"
            >
              <Globe2 className="h-3 w-3" /> Feed
            </NavLink>
            {user && (
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `${navLinkBase} ${isActive ? "text-white bg-white/5" : "text-white/50 hover:text-white"}`
                }
                data-testid="nav-library-link"
              >
                <LibraryIcon className="h-3 w-3" /> Library
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                <div className="hidden md:flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] text-white/50">
                  <span>{user.name || user.email}</span>
                  {user.is_verified && <VerifiedBadge size={12} />}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await logout();
                    navigate("/feed");
                  }}
                  className="rounded-sm border border-white/10 hover:border-[#FF3B30] hover:bg-transparent hover:text-white text-white/80 font-mono uppercase tracking-[0.2em] text-xs"
                  data-testid="logout-btn"
                >
                  <LogOut className="h-3.5 w-3.5 mr-2" /> Sign out
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  state={{ from: location.pathname }}
                  className="rounded-sm border border-white/10 hover:border-[#FF3B30] text-white/80 hover:text-white font-mono uppercase tracking-[0.2em] text-xs px-3 py-2 transition-colors"
                  data-testid="header-signin-link"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="rounded-sm bg-[#FF3B30] hover:bg-[#FF5C53] text-white font-display font-bold uppercase tracking-wide text-xs px-3 py-2 transition-colors"
                  data-testid="header-signup-link"
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="relative z-10">{children}</main>
    </div>
  );
}
